import React, {type ReactNode} from 'react';

import {SelectBaseRTK} from 'app/components/general/SelectBase/SelectBaseRTK';
import {
  ActionMeta,
  type CreatableProps as SelectProps,
} from 'app/components/general/SweetReactSelect';
import {useSearchResults} from 'app/search/api';
import {convertLocationParamsToFetchParams} from 'app/search/components/SearchDashboard';
import {getContentType} from 'app/utils/content-type';

import type {SearchItemType} from 'app/search/types';
import type {SearchSerializedType} from 'sweet-types';

type SelectPropsType = Partial<
  SelectProps<SearchSerializedType, string | number, ReactNode>
>;

interface CommonProps {
  disabled?: boolean;
  placeholder: string;
  refetchDataOnOpen?: boolean;
  creatable?: boolean;
  noOptionsMessage?: () => string;
  params?: Record<string, string | number>;
  selectProps?: SelectPropsType;
}

interface SingleItemProps extends CommonProps {
  selected: SearchSerializedType | null;
  queryParams?: {content_type: string; object_id: number | string};
  onChange: (value: SearchSerializedType | null) => Promise<unknown>;
}

export const SearchItemsSelectSingle = ({
  selected,
  queryParams,
  selectProps,
  ...other
}: SingleItemProps) => {
  const typeIcon = (value: SearchSerializedType) =>
    getContentType(value.content_type).icon;

  const searchItem: SearchSerializedType | null =
    useSearchResults(
      {
        type: queryParams?.content_type,
        object_id: queryParams?.object_id,
      },
      {
        refetchOnMountOrArgChange: false,
        skip: selected?.id != null || queryParams == null,
      }
    ).data?.results?.[0] ?? null;

  const baseSelectProps: SelectPropsType = {
    className: 'flex-grow-1',
    getOptionValue: (item: SearchSerializedType) => item.object_id,
    ...(selectProps ?? {}),
  };

  return (
    <SelectBaseRTK
      isMulti={false}
      isRTKFetcher={true}
      typeIcon={typeIcon}
      selected={
        selected != null ? selected : queryParams != null ? searchItem : null
      }
      useDataHook={useSearchResults}
      paramsConverter={convertLocationParamsToFetchParams}
      selectProps={baseSelectProps}
      translateSearchParams={(q: string) => ({
        q,
        ordering: q === '' ? 'name' : '-rank',
      })}
      {...other}
    />
  );
};

interface MultiItemProps extends CommonProps {
  selected: Array<SearchItemType>;
  onChange: (
    teams: Array<SearchItemType>,
    meta: ActionMeta<SearchItemType>
  ) => Promise<unknown>;
}

export const SearchItemsSelectMulti = ({
  selectProps = {},
  ...other
}: MultiItemProps) => {
  const typeIcon = (item: SearchItemType) =>
    getContentType(item.content_type).icon;

  return (
    <SelectBaseRTK
      isMulti={true}
      isRTKFetcher={true}
      typeIcon={typeIcon}
      useDataHook={useSearchResults}
      paramsConverter={convertLocationParamsToFetchParams}
      translateSearchParams={(q: string) => ({
        q,
        ordering: q === '' ? 'name' : '-rank',
      })}
      selectProps={selectProps}
      {...other}
    />
  );
};
